import { useTranslation } from 'react-i18next'
import { useState, useMemo, useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import { Button, ConfirmationModal, Flex, Loader, Modal, Typography } from '@percent/lemonade'
import { EligibilityCriteriaProps } from './EligibilityCriteria.types'
import { ErrorView } from '@percent/admin-dashboard/common/components'
import style from './EligibilityCriteria.module.scss'
import { TagsList } from './tagsList/TagsList'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'

export function EligibilityCriteria({
  program,
  isLoading,
  errorMessage,
  activityTags,
  isSaving,
  onSaveEligibilityCriteria
}: EligibilityCriteriaProps) {
  const { t } = useTranslation()
  const addNewProgramFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_ADD_NEW_PROGRAM)
  const { pathname } = useLocation()

  const isDuplicating = pathname.includes('duplicate')

  const activityTagsToDisplay = useMemo(
    () =>
      activityTags
        ?.map(tag => ({
          ...tag,
          subTags: tag.subTags.filter(subTag => !subTag.deprecatedAt)
        }))
        .filter(tag => tag.subTags.length > 0) ?? [],
    [activityTags]
  )

  const defaultSubTags = useMemo(
    () => program?.allowedActivitySubTags.map(tag => tag.id) || [],
    [program?.allowedActivitySubTags]
  )

  const defaultTags = useMemo(
    () =>
      activityTagsToDisplay
        ?.filter(tag => tag.subTags.map(subTag => subTag.id).some(subTagId => defaultSubTags?.includes(subTagId)))
        .map(tag => tag.id) || [],
    [activityTagsToDisplay, defaultSubTags]
  )

  const [isSaveModalOpened, setIsSaveModalOpened] = useState(false)
  const [subTagsForm, setSubTagsForm] = useState(defaultSubTags)
  const [tagsForm, setTagsForm] = useState(defaultTags)

  useEffect(() => {
    if (program?.allowedActivitySubTags) {
      setSubTagsForm(defaultSubTags)

      if (activityTagsToDisplay.length > 0) {
        setTagsForm(defaultTags)
      }
    }
  }, [program?.allowedActivitySubTags, defaultSubTags, defaultTags, activityTagsToDisplay.length])

  const isSaveBtnDisabled = useMemo(() => {
    if (isDuplicating) {
      return false
    }

    if (program?.allowedActivitySubTags && program?.allowedActivitySubTags?.length === subTagsForm.length) {
      const sortedPartnerSubTags = program.allowedActivitySubTags
        .map(tag => tag.id)
        .slice()
        .sort((a, b) => a.localeCompare(b))
      const sortedFormSubTags = subTagsForm.slice().sort((a, b) => a.localeCompare(b))

      if (sortedPartnerSubTags.every((value, index) => value === sortedFormSubTags[index])) {
        return true
      }
    }

    if (!program?.allowedActivitySubTags && subTagsForm?.length === 0) {
      return true
    }

    return false
  }, [program?.allowedActivitySubTags, isDuplicating, subTagsForm])

  if (isLoading) {
    return <Loader />
  }

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  const handleSaveEligibilityCriteria = async () => {
    await onSaveEligibilityCriteria(subTagsForm)
    setIsSaveModalOpened(false)
  }

  return (
    <div className={style.container}>
      <Modal open={isSaveModalOpened} onClose={() => setIsSaveModalOpened(false)}>
        <ConfirmationModal
          title={t('typography.editEligibilityCriteria')}
          primaryButtonText={t('button.save')}
          secondaryButtonText={t('button.cancel')}
          loading={isSaving}
          handleClose={() => setIsSaveModalOpened(false)}
          handleSubmit={handleSaveEligibilityCriteria}
          primaryBtnTestId="save-eligibility-criteria-submit-button"
          secondaryBtnTestId="save-eligibility-criteria-cancel-button"
          aria-labelledby="save-eligibility-criteria-modal"
        >
          {t('typography.saveEligibilityCriteriaConfirmation', {
            programName: program?.name || t('typography.unnamedProgram')
          })}
        </ConfirmationModal>
      </Modal>
      <div className={style.pageHeader}>
        <Flex justify="space-between">
          <Typography variant="h2" className={style.pageTitle}>
            {addNewProgramFeatureFlag
              ? t('typography.editEligibilityCriteria')
              : t('typography.seeEligibilityCriteria')}
          </Typography>
          {addNewProgramFeatureFlag && (
            <Button
              variant="secondary"
              disabled={isSaveBtnDisabled}
              size="small"
              onClick={() => setIsSaveModalOpened(true)}
            >
              {t('button.save')}
            </Button>
          )}
        </Flex>
        <Typography variant="default-medium" className={style.pageDescription}>
          {t('typography.editEligibilityCriteriaDescription', {
            configurationName: program?.name || t('typography.unnamedProgram')
          })}
        </Typography>
      </div>
      <TagsList
        activityTags={activityTags}
        tags={tagsForm}
        subTags={subTagsForm}
        onTagChange={setTagsForm}
        onSubTagChange={setSubTagsForm}
      />
    </div>
  )
}
